import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Currency } from '@/types';
import { useUpdateCurrencyMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface UpdateCurrencyProps extends DialogProps {
  currency: Currency;
  onClose(): void;
}

const UpdateCurrency: React.FC<UpdateCurrencyProps> = ({ currency, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateCurrency, updateCurrencyResponse] = useUpdateCurrencyMutation();

  const { values, errors, touched, handleSubmit, handleChange, resetForm } = useFormik<Currency>({
    initialValues: currency ?? {
      code: '',
      full_name: '',
      short_name: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      code: yup.string().required(messages.form.REQUIRED),
      full_name: yup.string().required(messages.form.REQUIRED),
      short_name: yup.string().required(messages.form.REQUIRED),
    }),
    async onSubmit(currency) {
      try {
        await updateCurrency(currency).unwrap();

        handleClose();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  function handleClose() {
    onClose();
    resetForm();
  }

  return (
    <Dialog onClose={handleClose} fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать валюту</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="code"
                label="Код"
                placeholder="Введите значение"
                value={values.code}
                onChange={handleChange}
                disabled={updateCurrencyResponse.isLoading}
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code ? errors.code : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="full_name"
                label="Полное наименование"
                placeholder="Введите значение"
                value={values.full_name}
                onChange={handleChange}
                disabled={updateCurrencyResponse.isLoading}
                error={touched.full_name && Boolean(errors.full_name)}
                helperText={touched.full_name ? errors.full_name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="short_name"
                label="Краткое наименование"
                placeholder="Введите значение"
                value={values.short_name}
                onChange={handleChange}
                disabled={updateCurrencyResponse.isLoading}
                error={touched.short_name && Boolean(errors.short_name)}
                helperText={touched.short_name ? errors.short_name : ''}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            size="large"
            disabled={updateCurrencyResponse.isLoading}
            onClick={handleClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" size="large" disabled={updateCurrencyResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateCurrency;
